import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, toConsumableArray as _toConsumableArray, objectSpread2 as _objectSpread2, extends as _extends, typeof as _typeof } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useEffect } from 'react';
import { AsyncCheckboxList } from '../Form/AsyncCheckboxList.js';
import { BackButton } from '../BackButton.js';
import { Button } from '../Button/index.js';
import { i18n } from '../../i18n/translate.js';
import kebabCase from 'lodash/kebabCase.js';
import { MaterialCheckbox } from '../../material/MaterialCheckbox.js';
import { noop } from '../../utils/noop.js';
import { onExecuteKeyDown } from '../../utils/KeyboardUtil.js';
import { selectComponents } from '../Form/Controls/SelectComponentWrappers.js';
import { Step } from '../Step.js';
import { Wizard } from '../Wizard.js';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import uniqBy from 'lodash/uniqBy';
import { BaseFilter } from './base-filter/component.js';
import { FAIcon } from '../fa-icon/component.js';
var _excluded = ["asyncOptions", "customComponent", "defaultValue", "explainerKey", "iconFamily", "iconKey", "isAsync", "isTwoStep", "labelKey", "onChange", "onClearFilters", "onMenuClose", "onMenuOpen", "options", "selectedKey", "showHelperText", "showSelected", "valueKey"];
var MAX_OPTIONS = 10;
var ClearIndicator = null;
var IndicatorSeparator = null;
var SelectFilter = function SelectFilter(props) {
  var _props$asyncOptions = props.asyncOptions,
    asyncOptions = _props$asyncOptions === void 0 ? {} : _props$asyncOptions,
    customComponent = props.customComponent,
    defaultValue = props.defaultValue,
    explainerKey = props.explainerKey,
    iconFamily = props.iconFamily,
    iconKey = props.iconKey,
    isAsync = props.isAsync,
    isTwoStep = props.isTwoStep,
    _props$labelKey = props.labelKey,
    labelKey = _props$labelKey === void 0 ? 'label' : _props$labelKey,
    _props$onChange = props.onChange,
    onChange = _props$onChange === void 0 ? noop : _props$onChange,
    _props$onClearFilters = props.onClearFilters,
    onClearFilters = _props$onClearFilters === void 0 ? noop : _props$onClearFilters,
    _props$onMenuClose = props.onMenuClose,
    onMenuClose = _props$onMenuClose === void 0 ? noop : _props$onMenuClose,
    _props$onMenuOpen = props.onMenuOpen,
    onMenuOpen = _props$onMenuOpen === void 0 ? noop : _props$onMenuOpen,
    _props$options = props.options,
    options = _props$options === void 0 ? [] : _props$options,
    selectedKey = props.selectedKey,
    showHelperText = props.showHelperText,
    showSelected = props.showSelected,
    _props$valueKey = props.valueKey,
    valueKey = _props$valueKey === void 0 ? 'id' : _props$valueKey,
    selectProps = _objectWithoutProperties(props, _excluded);
  var _useState = useState(defaultValue),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isOpen = _useState4[0],
    setIsOpen = _useState4[1];
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    wizardStep = _useState6[0],
    setWizardStep = _useState6[1];

  // this is the ID of the category that's currently being shown, if any
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    selectedCategoryId = _useState8[0],
    setSelectedCategoryId = _useState8[1];

  // this array holds a copy of the category object with the selected values for it
  // only categories that have options selected are included
  var _useState9 = useState([]),
    _useState0 = _slicedToArray(_useState9, 2),
    selectedCategories = _useState0[0],
    setSelectedCategories = _useState0[1];
  var _useState1 = useState([]),
    _useState10 = _slicedToArray(_useState1, 2),
    asyncData = _useState10[0],
    setAsyncData = _useState10[1];
  var isSelectingCategory = isTwoStep && wizardStep === 0;
  useEffect(function () {
    if (selectProps.value) {
      if (isTwoStep) {
        // copy selected category values from props to state
        var newCategories = options.reduce(function (acc, option) {
          var valueCategory = selectProps.value.find(function (val) {
            return val.id === option.id;
          });
          if (valueCategory) {
            var values = _toConsumableArray(valueCategory.values);
            acc.push(_objectSpread2(_objectSpread2({}, valueCategory), {}, {
              values: values
            }));
          }
          return acc;
        }, []);
        setSelectedCategories(newCategories);
      } else {
        setValue(selectProps.value);
      }
    }
  }, [selectProps.value, isTwoStep, options.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(function () {
    if (defaultValue) {
      if (isTwoStep) {
        // copy default category values from props to state
        // lookup and add any selected values in the category to the copied object
        var _selectedCategories = defaultValue.map(function (cat) {
          var category = options.find(function (opt) {
            return opt.id === cat.id;
          });
          if (category) {
            var values = cat.values.map(function (val) {
              var _category$categoryOpt;
              return (_category$categoryOpt = category.categoryOptions) === null || _category$categoryOpt === void 0 ? void 0 : _category$categoryOpt.find(function (opt) {
                return opt.id === val.id;
              });
            });
            return _objectSpread2(_objectSpread2({}, category), {}, {
              values: values
            });
          }
        }).filter(function (cat) {
          return cat;
        });
        if (_selectedCategories.length) {
          setSelectedCategories(_selectedCategories);
          onChange(_selectedCategories);
        }
      } else {
        setValue(defaultValue);
      }
    }
  }, [defaultValue, isTwoStep, options.length]); // eslint-disable-line react-hooks/exhaustive-deps

  var performSetValue = function performSetValue(val) {
    if (Array.isArray(val)) {
      val = val.filter(function (item) {
        return !(_typeof(item) === 'object' && Object.keys(item).length === 0);
      });
    }
    setValue(val);
    onChange(val);
  };
  var performSetCategoryValues = function performSetCategoryValues(newValues) {
    var selectedCategory = getSelectedCategory();
    var newCategories = selectedCategories.filter(function (cat) {
      return cat.id !== selectedCategoryId;
    });
    if (newValues.length > 0) {
      newCategories.push(_objectSpread2(_objectSpread2({}, selectedCategory), {}, {
        values: newValues
      }));
    }
    setValue(newCategories);
    setSelectedCategories(newCategories);
    onChange(newCategories);
  };
  var performTwoStepSetValue = function performTwoStepSetValue(option, action) {
    if (isSelectingCategory) {
      if (action === 'go-forward') {
        if (selectedCategoryId !== option.id) {
          setSelectedCategoryId(option.id);
        }
        setWizardStep(1);
      } else if (action === 'clear') {
        setSelectedCategories([]);
        onChange([]);
      }
    } else {
      var newValues;
      var selectedCategory = getSelectedCategory();
      var currentValues = selectedCategory.values || [];
      switch (action) {
        case 'deselect-option':
          newValues = currentValues.filter(function (val) {
            return val.id !== option.id;
          });
          break;
        case 'select-option':
          if (!currentValues.includes(function (val) {
            return val.id === option.id;
          })) {
            newValues = [].concat(_toConsumableArray(currentValues), [option]);
          } else {
            newValues = _toConsumableArray(currentValues);
          }
          break;
        case 'clear':
          setWizardStep(0);
          newValues = [];
          break;
      }
      performSetCategoryValues(newValues);
    }
  };
  var onSelectChange = function onSelectChange(val, action) {
    if (customComponent) {
      var toggleVal = val.id ? false : {
        id: true,
        label: selectProps.checkedLabel || "".concat(i18n.t('Filtering by'), " ").concat(selectProps.label)
      };
      performSetValue(toggleVal);
      setIsOpen(true);
      return;
    }
    if (!selectProps.isMulti && selectProps.value === val) {
      performSetValue({});
      setIsOpen(false);
      return;
    }
    if (isTwoStep) {
      performTwoStepSetValue(action.option, action.action);
      if (!selectProps.isMulti) setIsOpen(false);
      return;
    }
    switch (action.action) {
      case 'select-option':
        performSetValue(val);
        break;
      case 'deselect-option':
        performSetValue(!value || value.length === 1 || !Array.isArray(value) ? undefined : value.filter(function (value) {
          return value[valueKey] !== action.option[valueKey];
        }));
        break;
      case 'clear':
        performSetValue(undefined);
        break;
    }
    if (!selectProps.isMulti) {
      setIsOpen(false);
    }
  };
  var findInAsyncData = function findInAsyncData(keyField) {
    var selectValue = selectProps.value || value;
    var item = asyncData.find(function (item) {
      return String(item[valueKey]) === keyField;
    });

    // if it's not in the asyncData yet, it means it comes pre-selected
    return item || selectValue.find(function (item) {
      return String(item[valueKey]) === keyField;
    });
  };
  var onAsyncSelectChange = function onAsyncSelectChange(values) {
    var newValues = values.map(function (value) {
      return findInAsyncData(value);
    });
    if (isTwoStep) {
      performSetCategoryValues(newValues);
    } else {
      performSetValue(newValues);
    }
  };
  var onToggleOpen = function onToggleOpen(isOpen) {
    setIsOpen(isOpen);
    if (isOpen) {
      onMenuOpen();
    } else {
      onMenuClose();
    }
  };
  var IndicatorsContainer = function IndicatorsContainer() {
    var _selectProps$name$rep, _selectProps$name, _selectProps$name$rep2;
    var formattedName = (_selectProps$name$rep = selectProps === null || selectProps === void 0 || (_selectProps$name = selectProps.name) === null || _selectProps$name === void 0 || (_selectProps$name$rep2 = _selectProps$name.replace) === null || _selectProps$name$rep2 === void 0 ? void 0 : _selectProps$name$rep2.call(_selectProps$name, '[]', '')) !== null && _selectProps$name$rep !== void 0 ? _selectProps$name$rep : '';
    var name = formattedName ? "-".concat(formattedName) : '';
    var dataObjectId = "Select".concat(name, "-indicators");
    return /*#__PURE__*/React.createElement("div", {
      className: "filter-search-indicator",
      "data-object-id": dataObjectId
    }, /*#__PURE__*/React.createElement(FAIcon, {
      icon: "search"
    }));
  };
  var renderClearFilters = function renderClearFilters() {
    var setValue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : noop;
    var onClear = function onClear() {
      onClearFilters();
      setValue(undefined, 'clear');
    };
    return /*#__PURE__*/React.createElement("div", {
      className: "filter-clear"
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: onClear,
      onKeyDown: onExecuteKeyDown(onClear),
      style: "link",
      icon: "xmark",
      label: i18n.t('Clear Filter')
    }));
  };
  var Menu = function Menu(props) {
    return /*#__PURE__*/React.createElement(components.Menu, props, props.children, props.hasValue && selectProps.clearable && renderClearFilters(props.setValue));
  };
  Menu.propTypes = {
    hasValue: PropTypes.bool,
    setValue: PropTypes.func
  };
  var renderOptionLabel = function renderOptionLabel(data) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, iconKey && /*#__PURE__*/React.createElement(FAIcon, {
      icon: data[iconKey]
    }), /*#__PURE__*/React.createElement("div", {
      className: "option-container",
      "data-object-type": "menu-dropdown-options",
      "data-action": "select-option-".concat(kebabCase(data.label)),
      "data-object-id": data.name
    }, /*#__PURE__*/React.createElement("div", {
      className: "option-label"
    }, data[labelKey]), explainerKey && /*#__PURE__*/React.createElement("div", {
      className: "option-explainer"
    }, data[explainerKey])));
  };
  var isClearableSelect = !(selectProps.clearable === false || selectProps.isClearable === false);
  var isSearchable = !!options && options.length > MAX_OPTIONS;
  var Control = isSearchable ? components.Control : function () {
    return /*#__PURE__*/React.createElement("div", {
      tabIndex: 0
    });
  };
  var selectionVisible = !selectProps.isMulti && showSelected && !!value;
  var classPrefix = "Select".concat(selectProps !== null && selectProps !== void 0 && selectProps.name ? "-".concat(selectProps === null || selectProps === void 0 ? void 0 : selectProps.name) : '');
  var Option = function Option(props) {
    var _props$data;
    if (customComponent) {
      var CustomOptionComponent = customComponent.Option;
      return /*#__PURE__*/React.createElement(CustomOptionComponent, props);
    }
    if (isTwoStep) {
      if (isSelectingCategory) {
        var children = props.children,
          id = props.data.id;
        var selectedValues = selectedCategoryValues(id).length;
        return /*#__PURE__*/React.createElement(components.Option, props, /*#__PURE__*/React.createElement("div", {
          className: "option-text"
        }, children), /*#__PURE__*/React.createElement("div", {
          className: "option-right"
        }, selectedValues > 0 && /*#__PURE__*/React.createElement("span", {
          className: "option-count"
        }, selectedCategoryValues(id).length), /*#__PURE__*/React.createElement(FAIcon, {
          icon: "angle-right"
        })));
      } else {
        var _value = props.selectProps.value,
          _id = props.data.id;
        var isThisValueSelected = _value.find(function (val) {
          return val.id === _id;
        });
        return /*#__PURE__*/React.createElement(components.Option, _extends({}, props, {
          isSelected: !!isThisValueSelected
        }));
      }
    }
    var helperText = showHelperText ? (_props$data = props.data) === null || _props$data === void 0 ? void 0 : _props$data.helperText : null;
    return /*#__PURE__*/React.createElement("div", _extends({
      ref: props.innerRef
    }, props.innerProps, props), /*#__PURE__*/React.createElement(selectComponents.Option, _extends({
      helperText: helperText,
      labelClass: "m-0"
    }, props)));
  };
  var getSelectedCategory = function getSelectedCategory() {
    var id = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : selectedCategoryId;
    var selectedCategory = selectedCategories.find(function (cat) {
      return cat.id === id;
    });
    if (!selectedCategory) {
      selectedCategory = options.find(function (opt) {
        return opt.id === id;
      });
    }
    return selectedCategory;
  };
  var selectedCategoryValues = function selectedCategoryValues() {
    var id = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : selectedCategoryId;
    var selectedCategory = getSelectedCategory(id);
    return selectedCategory && selectedCategory.values || [];
  };
  var selectedCategoryOptions = function selectedCategoryOptions() {
    var id = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : selectedCategoryId;
    var selectedCategory = getSelectedCategory(id);
    return selectedCategory && selectedCategory.categoryOptions || [];
  };
  var selectedCategoryAsyncOptions = function selectedCategoryAsyncOptions() {
    var id = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : selectedCategoryId;
    var selectedCategory = getSelectedCategory(id);
    return selectedCategory && selectedCategory.asyncOptions || [];
  };
  var renderSelect = function renderSelect() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    var isMulti = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    var isAsync = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    var asyncOptions = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    var isOptionSelected = function isOptionSelected(option) {
      var _valueObject;
      if (isTwoStep && isSelectingCategory) {
        return selectedCategoryValues(option.id).length > 0;
      }
      if (!Array.isArray(value)) {
        if (typeof (value === null || value === void 0 ? void 0 : value[labelKey]) === 'string' && value[labelKey] === option[labelKey]) {
          return value[valueKey] === 'true';
        } else {
          return value === option[valueKey];
        }
      }
      var valueObject = value;
      if (isTwoStep) {
        var _value$find;
        valueObject = value === null || value === void 0 || (_value$find = value.find(function (obj) {
          return obj[valueKey] === selectedCategoryId;
        })) === null || _value$find === void 0 ? void 0 : _value$find.values;
      }
      return (_valueObject = valueObject) === null || _valueObject === void 0 ? void 0 : _valueObject.some(function (elem) {
        var item = elem[valueKey] || elem;
        return item === option[valueKey];
      });
    };
    var onChange = function onChange(val, action) {
      if (!isTwoStep || !isSelectingCategory || action.action === 'clear') {
        onSelectChange(val, action);
      } else {
        onSelectChange(action.option, {
          action: 'go-forward',
          option: action.option
        });
      }
    };
    var selectValue = selectProps.value || value;
    if (isTwoStep && isAsync) {
      var _currentCategoryValue;
      var wrapperValues = selectProps.value || value;
      var currentCategoryValues = wrapperValues === null || wrapperValues === void 0 ? void 0 : wrapperValues.find(function (wValue) {
        return (wValue === null || wValue === void 0 ? void 0 : wValue.id) === selectedCategoryId;
      });
      selectValue = (_currentCategoryValue = currentCategoryValues === null || currentCategoryValues === void 0 ? void 0 : currentCategoryValues.values) !== null && _currentCategoryValue !== void 0 ? _currentCategoryValue : currentCategoryValues;
    }
    var currentOptions = _toConsumableArray(options);
    var components = _objectSpread2(_objectSpread2({}, selectComponents), {}, {
      ClearIndicator: ClearIndicator,
      // null
      Control: Control,
      IndicatorsContainer: IndicatorsContainer,
      IndicatorSeparator: IndicatorSeparator,
      // null
      Menu: Menu,
      Option: Option
    });
    return isAsync ? /*#__PURE__*/React.createElement(AsyncCheckboxList, _extends({}, selectProps, asyncOptions, {
      CheckboxComponent: MaterialCheckbox,
      key: selectedCategoryId,
      labelKey: labelKey,
      onChange: onAsyncSelectChange,
      pageSize: asyncOptions.pageSize || MAX_OPTIONS,
      renderClearFilters: isClearableSelect ? renderClearFilters : null,
      searchWrapperClasses: "filter-search",
      selectedValues: selectValue ? selectValue.map(function (val) {
        return val[valueKey];
      }) : [],
      showSelectOptions: false,
      valueKey: valueKey,
      onDataLoaded: function onDataLoaded(data) {
        return setAsyncData(uniqBy([].concat(_toConsumableArray(asyncData), _toConsumableArray(data)), valueKey));
      }
    })) : /*#__PURE__*/React.createElement(Select, _extends({}, selectProps, {
      "aria-labelledby": "".concat(selectProps.name, "-label"),
      autoFocus: true,
      blurInputOnSelect: false,
      closeMenuOnSelect: false,
      hideSelectedOptions: false,
      backspaceRemovesValue: false,
      className: "filter-select",
      classNamePrefix: "filter-select",
      components: !explainerKey ? components : {
        Control: function Control() {
          return null;
        }
      },
      controlShouldRenderValue: false,
      formatOptionLabel: renderOptionLabel,
      inputId: "".concat(classPrefix, "-input"),
      instanceId: "".concat(selectProps.name ? "".concat(selectProps.name, "-") : '', "value"),
      isClearable: isClearableSelect,
      isMulti: isMulti,
      isSearchable: isSearchable,
      isOptionSelected: isOptionSelected,
      key: selectedCategoryId,
      menuIsOpen: true,
      name: selectProps.name,
      onChange: onChange,
      options: currentOptions !== null && currentOptions !== void 0 ? currentOptions : [],
      placeholder: i18n.t('Search'),
      tabSelectsValue: false,
      value: isTwoStep ? selectedCategoryValues() : selectValue,
      getOptionLabel: function getOptionLabel(item) {
        return item[labelKey];
      },
      getOptionValue: function getOptionValue(item) {
        return item[valueKey];
      }
    }));
  };
  var wizardSteps;
  if (isTwoStep) {
    wizardSteps = [/*#__PURE__*/
    // First step: category selection
    React.createElement(Step, {
      key: "step-1"
    }, /*#__PURE__*/React.createElement("div", {
      className: "step-1"
    }, renderSelect(options, true))), /*#__PURE__*/
    // Second step: value selection
    React.createElement(Step, {
      key: "step-2"
    }, renderSelect(selectedCategoryOptions(), true, isAsync, selectedCategoryAsyncOptions()))];
  }
  return /*#__PURE__*/React.createElement(BaseFilter, _extends({}, props, {
    disabled: props.isDisabled,
    isOpen: isOpen,
    onToggleOpen: onToggleOpen,
    selected: selectionVisible && value[selectedKey || labelKey] || '',
    selectedIcon: selectionVisible && iconKey && value[iconKey] || '',
    selectedIconFamily: iconFamily || ''
  }), isTwoStep ? /*#__PURE__*/React.createElement("div", {
    className: "two-step"
  }, /*#__PURE__*/React.createElement("h5", {
    className: "category-title"
  }, !isSelectingCategory ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BackButton, {
    action: function action() {
      setSelectedCategoryId(null);
      setWizardStep(0);
    }
  }), getSelectedCategory().label) : i18n.t('Select a Category')), /*#__PURE__*/React.createElement(Wizard, {
    wizardStep: wizardStep,
    initialStep: 0,
    onCancel: noop,
    onComplete: noop,
    hasStepIndicator: false,
    hasButtons: false,
    overrideSteps: true
  }, wizardSteps)) : renderSelect(options, selectProps.isMulti, isAsync, asyncOptions));
};
SelectFilter.displayName = "SelectFilter";
SelectFilter.propTypes = {
  asyncOptions: PropTypes.shape({
    authToken: PropTypes.string,
    dataUrl: PropTypes.string,
    queryFields: PropTypes.object,
    pageSize: PropTypes.number
  }),
  children: PropTypes.node,
  clearable: PropTypes.bool,
  customComponent: PropTypes.object,
  data: PropTypes.object,
  defaultValue: PropTypes.any,
  disabled: PropTypes.bool,
  explainerKey: PropTypes.string,
  iconFamily: PropTypes.string,
  iconKey: PropTypes.string,
  isAsync: PropTypes.bool,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  innerProps: PropTypes.object,
  innerRef: PropTypes.object,
  isTwoStep: PropTypes.bool,
  labelKey: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  onClearFilters: PropTypes.func,
  onMenuClose: PropTypes.func,
  onMenuOpen: PropTypes.func,
  options: PropTypes.array,
  selected: PropTypes.string,
  selectedKey: PropTypes.string,
  selectProps: PropTypes.object,
  showHelperText: PropTypes.bool,
  showSelected: PropTypes.bool,
  valueKey: PropTypes.string
};
export { SelectFilter };